/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Equipment, _Equipment } from '../../_shared/interfaces/equipment';
import { AppState } from '../../app.state';
import {
  findModel,
  getDeviceStatusFromEquip,
  getEquipAlertMap,
  errorDef,
  userHasRole,
  isServiceOrgAdmin,
  isPB,
  isDentalOrgAdmin,
  isDentalOrgUser,
  getEquipMaint,
  getTriggeredMaint,
  isHandpiece,
  handpieceIsDueForService,
  stopProp,
  findWorstError,
  isDentalEzUser,
  isChair,
  checkHasFilter,
} from '../../utils';
import { isGuestUser } from '../../authentication/authentication.actions';
import { get, last, sortBy } from 'lodash';
import styles, { cardStyles } from './manage-equipment.styles';
import { Org } from '../../_shared/interfaces/org';
import { EquipCard } from './card.component';
import { Location } from '../../_shared/interfaces/location';
import { TableList } from '../../_shared/table-list';
import { List } from 'antd';
import colors from '../../_shared/colors';
import _Icon from '../../_shared/icon';
import SharedStyles from '../../_shared/styles';
import { useState, MouseEvent } from 'react';
import { ModalEvent } from '../../_shared/modal-event';
import { RowList } from '../../_shared/row-list';
import { renderLabel } from '../../utils/mixins';
import { Footer } from '../../footer';
import { AIcon as Icon } from '../../_shared/icon';
import moment from 'moment';
import { MaintStatus } from '../../_shared/interfaces/equipmentStatus';
import { ColumnProps } from 'antd/lib/table';
import { Model } from '../../_shared/interfaces/model';

interface CardsProps {
  equipment: Equipment[];
  sState: AppState;
  loading: boolean;
  cardClick: (e: Equipment, fromEdit?: boolean) => void;
  pageSize?: number;
  hidePageSelect?: boolean;
  app?: any;
  // AER-1612: optional controlled pagination wired into Redux. Both the
  // antd `List` (Cards view) and `TableList` (grid view) are uncontrolled by
  // default and lose their current page on unmount; passing these props
  // makes them read/write the persisted page on `dash.view.equipPage`.
  currentPage?: number;
  setEquipPage?: (page: number) => void;
  // AER-1612 follow-up: controlled page size for the grid view's TableList.
  // The Cards view's pageSize is hard-coded (100) and has no user-facing
  // size selector, so these only meaningfully affect renderGrid().
  equipPageSize?: number;
  setEquipPageSize?: (pageSize: number) => void;
}

export const getSubCatsFromEquipment = (
  equipment: Equipment,
  sState: AppState
) => {
  const location = get(sState, 'dash.locations', []).find(
    (l: Location) => l.id === equipment.locationId
  );

  const serviceOrg = get(sState, 'dash.allOrgs', []).find(
    (l: Org) => l.id === equipment.serviceOrgId
  );

  const dentalOrg = get(sState, 'dash.allOrgs', []).find(
    (l: Org) => l.id === equipment.dentalOrgId
  );

  const model = get(sState, 'dash.models', []).find(
    (m: Model) => m.id === equipment.modelId
  );

  return {
    location,
    serviceOrg,
    dentalOrg,
    model,
  };
};

export const renderStatus = (props: {
  equipment: Equipment;
  loading: boolean;
  sState: AppState;
}) => {
  const { loading, equipment } = props;
  const deviceState = getDeviceStatusFromEquip(equipment);
  const isHand = isHandpiece(equipment);
  const chair = isChair(equipment);
  const opt: {
    [key: string]: { color: string; label: string; canDisplay?: boolean };
  } = {
    loading: { color: colors.statusOff, label: 'loading' },
    off: { color: colors.statusOff, label: 'Offline' },
    debug: {
      color: colors.statusDebug,
      label: 'Debug',
      canDisplay: userHasRole([0], props.sState),
    },
    maint: { color: colors.statusMaint, label: 'Maint Mode' },
    standby: {
      color: colors.statusStandby,
      label: chair || isHand ? 'Idle' : 'Standby',
    },
    on: { color: colors.statusOn, label: chair || isHand ? 'In Use' : 'On' },
    highPressure: { color: colors.statusStandby, label: 'High Pressure' },
    highAltitude: { color: colors.statusStandby, label: 'High Altitude' },
    headsEnabled: {
      color: colors.statusDebug,
      label: 'Heads Disabled',
      canDisplay: !isHand && !chair,
    },
    scheduleEnabled: { canDisplay: false, label: '', color: '' },
    highVoltage: { color: colors.statusStandby, label: 'Power Up Override' },
  };
  const renderLabel = (option: { label: string; color: string }) => {
    return (
      <div
        key={option.label}
        css={css(cardStyles.cardStatus, { backgroundColor: option.color })}
      >
        {option.label}
        {loading && <Icon css={css('margin-left: 2px;')} type="loading" />}
      </div>
    );
  };

  return (
    <div css={css(cardStyles.statusContainer)}>
      {!loading && equipment
        ? Object.keys(deviceState).map(k => {
            let obj = deviceState[k];
            if (k === 'headsEnabled') {
              obj = !obj;
            }
            const sel = opt[k];
            const canDisplay =
              sel !== undefined && sel.canDisplay !== undefined
                ? sel.canDisplay
                : true;
            return !obj || !sel || !canDisplay ? null : renderLabel(sel);
          })
        : !loading
        ? renderLabel(opt.off)
        : renderLabel(opt.loading)}
    </div>
  );
};

export const RenderAlert = (props: { equipment: Equipment }) => {
  const alert = findWorstError(
    get(props, 'equipment.status.alerts'),
    props.equipment
  );
  const alertCode = errorDef(get(alert, 'error', 0));

  const [modalVisible, setModalVisible] = useState(false);

  return alert ? (
    <div
      css={css`
        margin-left: 5px;
        margin-right: 5px;
      `}
    >
      <_Icon
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
          setModalVisible(true);
        }}
        name="exclamation-triangle"
        color={alertCode.color}
        size={16}
      />

      <ModalEvent
        onClose={(e?: MouseEvent) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          setModalVisible(false);
        }}
        allowClear={true}
        visible={modalVisible}
        equipment={props.equipment}
        publish={{
          data: alert,
          published_at: moment(get(alert, 'time') * 1000).toISOString(),
        }}
        onClear={() => setModalVisible(false)}
        isActive={true}
      />
    </div>
  ) : null;
};

export const RenderConsignment = (props: {
  equipment: Equipment;
  table?: boolean;
}) => {
  const { onConsignment } = props.equipment;
  return onConsignment ? (
    <div
      css={css(
        props.table
          ? styles.consignmentContainerTable
          : styles.consignmentContainer
      )}
    >
      <p css={css(styles.consignmentText)}>C</p>
    </div>
  ) : null;
};

export const Cards = (props: CardsProps) => {
  const { equipment, sState, loading, cardClick, currentPage, setEquipPage } = props;
  const ispb = isPB(sState);
  // AER-1592: on mobile, Dental Org users see the equipment list grouped by
  // Location. Admins of a DO are covered by isDentalOrgAdmin; isDentalOrgUser
  // already includes role 6 (Org Read Only). Both branches imply DO orgType.
  const groupByLocation = ispb && (isDentalOrgUser(sState) || isDentalOrgAdmin(sState));
  const hasFilter = checkHasFilter(sState);

  const renderCard = (equip: Equipment) => {
    const cats = getSubCatsFromEquipment(equip, sState);
    const model = findModel(equip, sState);
    const config = get(sState, `dash.equipMap.${equip.deviceId}.configs.0`);
    const alerts = get(sState, `dash.equipMap.${equip.deviceId}.alerts`, []);
    const alertMap = get(
      sState,
      `dash.equipMap.${equip.deviceId}.alertMap`,
      {}
    );

    return (
      <EquipCard
        key={equip.id}
        location={cats.location}
        serviceOrg={cats.serviceOrg}
        dentalOrg={cats.dentalOrg}
        config={config}
        alerts={alerts}
        alertMap={alertMap}
        onClick={() => cardClick(equip)}
        onEditClick={() => cardClick(equip, true)}
        equipment={equip}
        model={model}
        loading={loading}
        sState={sState}
      />
    );
  };

  // Mobile: show "Equipment Count" + "Connected" stats at bottom of page,
  // matching web app's role-based logic. activeCount is only populated for
  // SuperAdmin / TechAdmin / DentalEZ Read Only (set in dashboard.actions.ts).
  const renderMobileCount = () => {
    const count = equipment.length;
    let connectedExtra = '';
    if (get(sState, 'dash.activeCount.connected')) {
      const { total, connected } = get(sState, 'dash.activeCount', {}) as any;
      connectedExtra = `Connected: ${connected} / ${total} (${(
        (connected / total) *
        100
      ).toFixed(2)}%)`;
    }
    return (
      <div css={css(styles.mobileCountContainer)}>
        <span>Equipment Count: {count}</span>
        {connectedExtra && (
          <span css={css('margin-left: 12px;')}>{connectedExtra}</span>
        )}
      </div>
    );
  };

  // AER-1592: groups (already-sorted+filtered) equipment by location for
  // Dental Org users on mobile. Locations come from dash.locations (scoped to
  // the user's org for DO users). When a filter is active, location groups
  // with no matching equipment are hidden; otherwise empty groups render the
  // "Location is not associated with any Equipment" message used by Admin.
  const renderGroupedByLocation = () => {
    const locations = get(sState, 'dash.locations', []) as Location[];

    const equipByLoc = new Map<string, Equipment[]>();
    equipment.forEach((eq: Equipment) => {
      const locId = eq.locationId || '__noloc__';
      const arr = equipByLoc.get(locId) || [];
      arr.push(eq);
      equipByLoc.set(locId, arr);
    });

    const groups = locations
      .slice()
      .sort((a: Location, b: Location) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      )
      .map((loc: Location) => ({
        location: loc,
        items: equipByLoc.get(loc.id) || [],
      }));

    // Equipment whose locationId doesn't match any of the user's locations:
    // surface under an "Unassigned" group only when there's something to show
    // (no empty placeholder for this synthetic group).
    const unassigned = equipByLoc.get('__noloc__') || [];

    const visibleGroups = hasFilter
      ? groups.filter(g => g.items.length > 0)
      : groups;

    return (
      <div>
        {visibleGroups.map(({ location, items }) => (
          <div key={location.id} css={css(styles.locationGroup)}>
            <div css={css(styles.locationGroupHeader)}>
              {/* AER-1592: just the location name — Bruce's spec says
                  "I kind of like it how it is - just clean / simple" and
                  Ted's mock only shows the name, no address. */}
              <p css={css(styles.locationGroupName)}>{location.name}</p>
            </div>
            {items.length === 0 ? (
              <p css={css(styles.locationEmpty)}>
                Location is not associated with any Equipment
              </p>
            ) : (
              items.map(renderCard)
            )}
          </div>
        ))}
        {unassigned.length > 0 && (
          <div css={css(styles.locationGroup)}>
            <div css={css(styles.locationGroupHeader)}>
              <p css={css(styles.locationGroupName)}>Unassigned</p>
            </div>
            {unassigned.map(renderCard)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      css={css(
        styles.cardContainer,
        `position: relative;`,
        ispb && 'margin-top: 0; padding-bottom: 0;'
      )}
    >
      {groupByLocation ? (
        renderGroupedByLocation()
      ) : (
        <List
          grid={{ gutter: ispb ? 4 : 16, column: ispb ? 1 : 4 }}
          dataSource={equipment}
          renderItem={renderCard}
          pagination={{
            pageSize: 100,
            size: 'small',
            // AER-1612: controlled current page so it survives unmount when
            // the user navigates into an equipment item and back.
            current: currentPage,
            onChange: (page: number) => {
              if (setEquipPage) {
                setEquipPage(page);
              }
              const el = document.getElementById('ContentContainer');
              if (el) el.scrollTop = 0;
              if (typeof window !== 'undefined') window.scrollTo(0, 0);
            },
          }}
        />
      )}
      {ispb && renderMobileCount()}
      {ispb && <Footer shouldExtend={true} />}
    </div>
  );
};

const Columns = (props: CardsProps) => {
  const showServiceOrgCol = () => {
    const isSuper = userHasRole([0, 1, 7], props.sState);
    if (isSuper) {
      return true;
    } else {
      return false;
    }
  };
  const showDentalOrgCol = () => {
    const isSuper = userHasRole([0, 1, 7], props.sState);
    const isSOAdmin = isServiceOrgAdmin(props.sState);

    if (isSuper || isSOAdmin) {
      return true;
    } else {
      return false;
    }
  };
  const ispb = isPB(props.sState);
  const isDentalEz = isDentalEzUser(props.sState);
  const isGuest = isGuestUser(props.sState);
  // AER-1597: hide Edit pencil for Org Read Only (6) / DentalEZ Read Only (7)
  // to match Equipment Details sidebar gating.
  const isReadOnlyRole = userHasRole([6, 7], props.sState);

  const cols = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: '20%',
      render: (text: string, e: Equipment) => {
        const alertMap = getEquipAlertMap(e, props.sState);
        return (
          <div css={css(SharedStyles.row)}>
            {text}
            <RenderAlert equipment={e} />
            <RenderConsignment equipment={e} table={true} />
          </div>
        );
      },
    },
    !ispb &&
      isDentalEz && {
        title: 'Model',
        dataIndex: 'modelId',
        width: '12%',
      },
    !ispb &&
      !isDentalEz && {
        title: 'Model',
        dataIndex: 'modelId',
        width: '12%',
        render: (text: string, e: Equipment) => {
          const cats = getSubCatsFromEquipment(e, props.sState);
          return get(cats, 'model.displayName', '');
        },
      },
    !ispb &&
      showServiceOrgCol() && {
        title: 'Service Org',
        dataIndex: 'serviceOrgId',
        width: '15%',
        render: (text: string, e: Equipment) => {
          const cats = getSubCatsFromEquipment(e, props.sState);
          return get(cats, 'serviceOrg.name', '');
        },
      },
    !ispb && {
      title: 'Service Due',
      dataIndex: 'id',
      width: '10%',
      render: (text: string, e: Equipment) => {
        const sortedMaint = sortBy(
          get(e, 'status.maint', []) as MaintStatus[],
          'timeDiff'
        );
        const isServiceDue = !!sortedMaint.find(m => m.timeDiff > 0);
        const isServiceOverDue = !!sortedMaint.find(m => m.timeDiff <= 0);
        const timeUntilServiceDueHours = get(last(sortedMaint), 'timeDiff');

        return isServiceOverDue ? (
          <p css={css(styles.serviceOverdueText)}>Service Overdue</p>
        ) : isServiceDue ? (
          `Service Due: ${timeUntilServiceDueHours} hours`
        ) : (
          '--'
        );
      },
    },
    !ispb &&
      showDentalOrgCol() && {
        title: 'Dental Org',
        dataIndex: 'dentalOrgId',
        width: '15%',
        render: (text: string, e: Equipment) => {
          const cats = getSubCatsFromEquipment(e, props.sState);
          return get(cats, 'dentalOrg.name', '');
        },
      },
    !ispb && {
      title: 'Location',
      dataIndex: 'locations.0',
      width: '15%',
      render: (text: string, e: Equipment) => {
        const cats = getSubCatsFromEquipment(e, props.sState);
        return get(cats, 'location.name', '');
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '20%',
      render: (text: string, e: Equipment) => {
        return renderStatus({
          equipment: e,
          loading: props.loading,
          sState: props.sState,
        });
      },
    },
    !ispb && {
      title: ' ',
      dataIndex: 'controllerSerial',
      width: '3%',
      render: (text: string, eq: Equipment) => {
        if (eq.isSmartChair || isGuest || isReadOnlyRole) {
          return null;
        }
        return (
          <Icon
            onClick={(e: MouseEvent) => {
              stopProp(e);
              props.cardClick(eq, true);
            }}
            type={'edit'}
            css={css(`color: ${props.app.getColor('primary')}`)}
          />
        );
      },
    },
  ].filter(c => c);

  return cols;
};

export const renderGrid = (props: CardsProps) => {
  const {
    equipment,
    cardClick,
    pageSize,
    hidePageSelect,
    currentPage,
    setEquipPage,
    equipPageSize,
    setEquipPageSize,
  } = props;

  const columns = Columns(props) as ColumnProps<any>[];
  const ispb = isPB(props.sState);
  // AER-1597: gate mobile expanded-row Edit icon to match Equipment Details
  // sidebar (hidden for Org Read Only / DentalEZ Read Only roles).
  const isReadOnlyRole = userHasRole([6, 7], props.sState);

  const editClick = (e: MouseEvent, EQ: Equipment) => {
    e.preventDefault();
    e.stopPropagation();

    cardClick && cardClick(EQ, true);
  };

  const countExtra = (() => {
    if (get(props, 'sState.dash.activeCount.connected')) {
      const { total, connected } = get(props, 'sState.dash.activeCount', {}) as any;
      return `Connected: ${connected} / ${total} (${((connected / total) * 100).toFixed(
        2
      )}%)`;
    }
  })();

  return (
    <div>
      {equipment.length === 0 && <Icon type="loading" />}
      {equipment.length > 0 && !ispb && (
        <TableList
          canSelectRows={false}
          columns={columns}
          data={equipment as never[]}
          rowClick={(e: Equipment) => cardClick(e)}
          pageSize={pageSize}
          hidePageSelect={hidePageSelect}
          includeCount="Equipment Count"
          countExtra={countExtra}
          // AER-1612: controlled current page wired through Redux so the
          // Dashboard's pagination is preserved across in-app navigation.
          currentPage={currentPage}
          onPageChange={setEquipPage}
          // AER-1612 follow-up: same idea for the user-chosen page size
          // ("20 / page", etc.). Only wired here — Cards view has no size
          // selector.
          controlledPageSize={equipPageSize}
          onPageSizeChange={setEquipPageSize}
        />
      )}
      {equipment.length > 0 && ispb && (
        <RowList
          columns={columns}
          data={equipment as never[]}
          rowClick={(e: Equipment) => cardClick(e)}
          renderOpen={_eq => {
            const eq = new _Equipment(_eq);
            const isDOAdmin = isDentalOrgAdmin(props.sState);
            const isSOAdmin = isServiceOrgAdmin(props.sState);
            const model = findModel(eq, props.sState);
            const { serviceOrg, dentalOrg, location } = getSubCatsFromEquipment(
              eq,
              props.sState
            );

            return (
              <div css={css(cardStyles.cardText)}>
                {renderLabel('modelName', {
                  obj: eq,
                  prefix: 'Model: ',
                  get: _val =>
                    get(model, 'name', get(model, 'id', 'Osprey Compressor')),
                })}
                {renderLabel('locationId', {
                  obj: eq,
                  prefix: 'Location: ',
                  get: (_val: string) => get(location, 'name'),
                })}
                {!isDOAdmin &&
                  !isSOAdmin &&
                  renderLabel('serviceOrgId', {
                    obj: eq,
                    prefix: 'Service Org: ',
                    get: (_val: string) => get(serviceOrg, 'name'),
                  })}
                {!isDOAdmin &&
                  renderLabel('dentalOrgId', {
                    obj: eq,
                    prefix: 'Dental Org: ',
                    get: (_val: string) => get(dentalOrg, 'name'),
                  })}
                {renderLabel('name', {
                  obj: eq,
                  get: (_val: string) => ' ',
                })}
                {!eq.isSmartChair && !isReadOnlyRole && (
                  <Icon
                    onClick={(e: any) => editClick(e, eq)}
                    type={'edit'}
                    css={css(
                      `color: ${colors.highlight}; position: absolute; bottom: 3px; right: 16px;`
                    )}
                  />
                )}
              </div>
            );
          }}
        />
      )}
    </div>
  );
};
